import React, { useMemo, useEffect, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import {
  AppBar,
  Box,
  Container,
  CssBaseline,
  Toolbar,
  useMediaQuery,
} from '@mui/material';

import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';
import PerfectScrollbar from 'react-perfect-scrollbar';
import navigation from 'menu-items';
import useConfig from 'hooks/useConfig';
import { drawerWidth } from 'store/constant';
import { openDrawer } from 'store/slices/menu';
import { useDispatch, useSelector } from 'store';
import { getDefaultEntityId } from 'store/slices/user';
import { fetchRecord } from 'services/AxiosAdmin';
import { IconChevronRight } from '@tabler/icons';

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    ...theme.typography.mainContent,
    ...(!open && {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
      [theme.breakpoints.up('lg')]: {
        marginLeft: -(drawerWidth - 20),
        width: `calc(100% - ${drawerWidth}px)`,
      },
      [theme.breakpoints.down('lg')]: {
        marginLeft: '10px',
        width: `calc(100% - ${drawerWidth}px)`,
        marginRight: '10px',
      },
    }),
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.shorter,
      }),
      marginLeft: 0,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      width: `calc(100% - ${drawerWidth}px)`,
      [theme.breakpoints.down('lg')]: {
        marginLeft: '10px',
        marginRight: '10px',
      },
    }),
  })
);

const MainLayout = () => {
  const theme = useTheme();
  const ps = useRef();
  const matchDownLg = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { drawerOpen, scrollTop } = useSelector((state) => state.menu);
  const { container } = useConfig();

  // Scroll to top on route change or scrollTop trigger
  const psScrollTop = () => {
    const curr = ps.current;
    if (curr) {
      curr.scrollTop = 0;
    }
  };

  // Open/close drawer based on screen size
  useEffect(() => {
    dispatch(openDrawer(!matchDownLg));
  }, [matchDownLg, dispatch]);

  // Fetch default entity ID
  useEffect(() => {
    dispatch(getDefaultEntityId());
  }, [dispatch]); // Fixed: added dispatch

  // Scroll to top
  useEffect(() => {
    psScrollTop();
  }, [navigate, scrollTop]);

  // Check entity status on mount if entityId exists
  useEffect(() => {
    const entityId = sessionStorage.getItem('entityId');
    if (!entityId) return;

    const checkStatus = async () => {
      try {
        const response = await fetchRecord(`/admin/entity/${entityId}`);
        if (response.status === 200) {
          const isActive = response.data.data.isEntityActive;
          if (!isActive) {
            sessionStorage.clear();
            window.close();
            // navigate('/login'); // Uncomment if needed
          }
        } else {
          // navigate('/entities'); // Uncomment if needed
        }
      } catch (error) {
        console.error('Failed to check entity status:', error);
        // navigate('/entities');
      }
    };

    checkStatus();
  }, []); // Runs only once on mount

  const header = useMemo(
    () => (
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Header />
      </Toolbar>
    ),
    []
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {/* Header */}
      <AppBar
        enableColorOnDark
        position='fixed'
        color='inherit'
        elevation={0}
        sx={{
          bgcolor: theme.palette.background.default,
          transition: drawerOpen ? theme.transitions.create('width') : 'none',
        }}
      >
        {header}
      </AppBar>

      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <Main theme={theme} open={drawerOpen} sx={{ padding: '20px 10px' }}>
        <PerfectScrollbar
          component='div'
          style={{
            height: 'calc(100vh - 148px)',
            padding: '0 10px',
          }}
          containerRef={(el) => (ps.current = el)}
          className='perfect-scroll-bar'
        >
          {container ? (
            <Container maxWidth='lg'>
              <Breadcrumbs
                separator={IconChevronRight}
                navigation={navigation}
                icon
                title
                rightAlign
              />
              <Outlet />
            </Container>
          ) : (
            <>
              <Breadcrumbs
                separator={IconChevronRight}
                navigation={navigation}
                icon
                title
                rightAlign
              />
              <Outlet />
            </>
          )}
        </PerfectScrollbar>
      </Main>
    </Box>
  );
};

export default MainLayout;
